import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal, Button, Badge } from 'react-bootstrap';

const sections = [
  {
    title: 'Start Here',
    summary: 'Learn the main navigation, account setup flow, and what read-only mode means.',
    details: {
      overview:
        'This app is organized around a few core work areas: contacts, tasks, listings, account settings, and team collaboration. Most users should set up Account first, then start building contacts and tasks.',
      steps: [
        'Open Account to confirm your user information, password, billing status, and team membership.',
        'Use the sidebar as the main navigation for daily work. Contacts and Tasks are the fastest place to start.',
        'If billing is inactive, the account remains readable, but create, update, and delete actions are blocked until the subscription is active again.'
      ],
      tips: [
        'If something appears locked, check the billing section in Account first.',
        'If you are working with a team, confirm you joined the correct business before using messages or shared boards.',
        'Use the dashboard as the summary view and the detail pages as the place to make changes.'
      ]
    }
  },
  {
    title: 'Contacts',
    summary: 'Create, review, and update contact records without losing existing data.',
    details: {
      overview:
        'Contacts are the center of the CRM. Each record can hold identity details, mailing information, notes, linked properties, preferences, and transaction-related fields.',
      steps: [
        'Create a contact from the Contacts area, a form workflow, or an approved bot/API workflow.',
        'Open the contact detail view to review notes, address fields, linked properties, tasks, and deal information.',
        'When editing, you can update only the fields you want to change. Missing fields should stay untouched under the new patch behavior.'
      ],
      tips: [
        'Duplicate checks are scoped to the same user, not globally across the whole app.',
        'Use a stable phone number or email when you want automations and upsert flows to find the same person again.',
        'If a contact is connected to a property or transaction, review those linked sections before deleting anything.'
      ]
    }
  },
  {
    title: 'Tasks',
    summary: 'Attach follow-up work to contacts and keep due dates and urgency visible.',
    details: {
      overview:
        'Tasks are designed for next actions. They help convert conversations into trackable work and keep follow-up from slipping between contacts, listings, and team handoffs.',
      steps: [
        'Create a task from a contact whenever a follow-up belongs to a specific person or lead.',
        'Set the task type, description, due date, urgency, and status clearly enough that another team member could pick it up.',
        'Move work through open, pending, in-progress, and completed states instead of creating duplicate tasks for the same item.'
      ],
      tips: [
        'Use due dates consistently so sorting surfaces the right work first.',
        'Keep task descriptions actionable. “Call buyer re: inspection timeline” is better than “follow up.”',
        'Closed tasks remain available for audit history and should not be used as a substitute for deleting records.'
      ]
    }
  },
  {
    title: 'Listings And Properties',
    summary: 'Understand the difference between listing records, property records, and linked contacts.',
    details: {
      overview:
        'Listings and properties are related but not identical. A property holds the physical/address details, while a listing holds market-facing data such as status, pricing, and MLS information.',
      steps: [
        'Use My Listings for records owned by the signed-in user and All Listings when you need the broader view.',
        'Open a property page when you need the full address, parcel, physical specs, linked contacts, and listing history together.',
        'When editing, confirm whether the field belongs to the property record or the listing record before saving changes.'
      ],
      tips: [
        'Status updates like Active, Pending, and Sold affect the listing record, not the base property record.',
        'If a listing is linked to a contact, confirm ownership and linkage before reassigning it.',
        'When something looks inconsistent, compare the listing fields and property fields separately.'
      ]
    }
  },
  {
    title: 'Team Features',
    summary: 'Set up a business or team, invite members, and use shared communication tools.',
    details: {
      overview:
        'Team functionality is built around an active team or brokerage membership. Shared messages, bulletin boards, and join workflows depend on that relationship being present and active.',
      steps: [
        'Create or update your business in Account before trying to invite members or use shared team spaces.',
        'Use search and join-request flows when you need to join an existing team instead of creating a second one.',
        'Owners and admins should review member access and pending join requests regularly.'
      ],
      tips: [
        'A user should only belong to one active team at a time.',
        'Messages and shared board features enforce team scope, so missing access usually means the account is not on the right team.',
        'If a user cannot see a shared area, confirm both team membership and account role.'
      ]
    }
  },
  {
    title: 'Bot And API Access',
    summary: 'Use API keys, supported operations, and read-only subscription rules correctly.',
    details: {
      overview:
        'The bot/API layer is meant for controlled automation, not unrestricted database access. It supports a defined set of operations with validation, ownership checks, and subscription gating.',
      steps: [
        'Create a bot API key from Account only when you need an external integration or automation.',
        'Use the published bot instructions and supported operation list as the source of truth for payload shapes and allowed actions.',
        'Give the bot your api key and the bot instructions to have your bot enter contacts, listings and add tasks. The bot instructions are the source of truth for what the bot can do.',
        'Remember that read operations can continue in read-only subscription mode, but write operations are blocked until billing is active.'
      ],
      tips: [
        'Upload a pdf of your listing details to you bot and have the bot enter into the system.',
        'If an automation fails, inspect whether the problem is validation, ownership, or subscription status before assuming a server bug.'
      ]
    }
  }
];

function HelpGuide() {
  const [activeSectionTitle, setActiveSectionTitle] = useState(null);

  const activeSection = useMemo(
    () => sections.find((section) => section.title === activeSectionTitle) || null,
    [activeSectionTitle]
  );

  return (
    <div className="container py-4">
      <div
        className="rounded-4 p-4 p-md-5 mb-4 text-white"
        style={{
          background: 'linear-gradient(135deg, rgba(13, 33, 38, 0.96), rgba(17, 77, 82, 0.9))',
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.18)'
        }}
      >
        <div className="d-flex flex-column flex-lg-row justify-content-between gap-3 align-items-lg-end">
          <div>
            <p className="text-uppercase small mb-2" style={{ letterSpacing: '0.18em', opacity: 0.8 }}>
              Help Center
            </p>
            <h1 className="display-6 fw-semibold mb-2">Guide To Using The Site</h1>
            <p className="mb-0" style={{ maxWidth: '760px', opacity: 0.92 }}>
              Use this page as the operating manual for account setup, contacts, tasks, listings, teams, and automations. Each card gives the short version, and the More button opens the full guide.
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Link className="btn btn-light" to="/account">
              Go To Account
            </Link>
            <Link className="btn btn-outline-light" to="/home">
              Back To Dashboard
            </Link>
          </div>
        </div>
      </div>

      <div className="row g-4">
        {sections.map((section) => (
          <div className="col-12 col-lg-6" key={section.title}>
            <section
              className="h-100 rounded-4 border p-4 bg-white d-flex flex-column"
              style={{ boxShadow: '0 14px 34px rgba(15, 23, 42, 0.07)' }}
            >
              <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                <h2 className="h4 mb-0">{section.title}</h2>
                <Badge bg="dark" pill>
                  Guide
                </Badge>
              </div>
              <p className="text-muted mb-3">{section.summary}</p>
              <ul className="ps-3 mb-4">
                {section.details.steps.slice(0, 2).map((item) => (
                  <li key={item} className="mb-2">
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-auto">
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={() => setActiveSectionTitle(section.title)}
                >
                  More
                </button>
              </div>
            </section>
          </div>
        ))}
      </div>

      <section className="rounded-4 border p-4 mt-4 bg-light">
        <h2 className="h4 mb-3">Need More Help?</h2>
        <p className="mb-3">
          If the guide does not answer the question, use the account dropdown and choose <strong>Report Bug / Make Suggestion</strong>.
        </p>
        <div className="d-flex flex-wrap gap-2">
          <Link className="btn btn-dark" to="/contacts">
            Open Contacts
          </Link>
          <Link className="btn btn-outline-dark" to="/tasks">
            Open Tasks
          </Link>
          <Link className="btn btn-outline-dark" to="/myListings">
            Open My Listings
          </Link>
        </div>
      </section>

      <Modal
        show={!!activeSection}
        onHide={() => setActiveSectionTitle(null)}
        centered
        size="lg"
      >
        {activeSection && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>{activeSection.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p className="mb-4">{activeSection.details.overview}</p>

              <h3 className="h5 mb-3">How To Use It</h3>
              <ol className="ps-3 mb-4">
                {activeSection.details.steps.map((step) => (
                  <li key={step} className="mb-2">
                    {step}
                  </li>
                ))}
              </ol>

              <h3 className="h5 mb-3">Important Notes</h3>
              <ul className="ps-3 mb-0">
                {activeSection.details.tips.map((tip) => (
                  <li key={tip} className="mb-2">
                    {tip}
                  </li>
                ))}
              </ul>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setActiveSectionTitle(null)}>
                Close
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </div>
  );
}

export default HelpGuide;
